/**
 * The website's display helpers: the Mini App's pure detail formatters
 * (one copy of every price, terms and location rule) plus the visitor's time
 * zone named in words. Every clock time a piece draws keeps its number and AM
 * or PM on one line (`keepTimes`).
 */
export {
  eventPriceText as amountText,
  formatDuration,
  formatLocation,
  getClientTimezone,
  paymentTermsText,
  servicePriceText as priceText,
  tzAbbrev,
} from '@/pages/SessionDetailPage/detailFormat';
import { formatSlotTime as slotTime, tzAbbrev } from '@/pages/SessionDetailPage/detailFormat';

/** Each clock time in `text` with its number and AM or PM held on one line (a browser's Intl puts a plain space between them). */
export function keepTimes(text: string): string {
  return text.replace(/(\d:\d\d)\s+([AP]M)\b/gi, '$1\u00a0$2');
}

/** A start's clock time in `tz`: "1:30 PM", held on one line. */
export function formatSlotTime(iso: string, tz: string): string {
  return keepTimes(slotTime(iso, tz));
}

/**
 * Each start as the clock shows it in `tz`, with the zone abbreviation added
 * where two starts share a clock time: on a fall-back day 1:30 AM happens
 * twice ("1:30 AM EDT", "1:30 AM EST").
 */
export function slotTimeLabels(starts: readonly string[], tz: string): string[] {
  const plain = starts.map((s) => formatSlotTime(s, tz));
  return plain.map((label, i) =>
    plain.indexOf(label) === plain.lastIndexOf(label) ? label : `${label} ${tzAbbrev(tz, new Date(starts[i]!))}`,
  );
}

/** The zone in words, as the Profile pick reads it: "Eastern Time (EDT)". Falls back to the zone id. */
export function zoneName(tz: string, ref: Date = new Date()): string {
  const part = (style: 'longGeneric' | 'short') => {
    try {
      return new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: style })
        .formatToParts(ref)
        .find((p) => p.type === 'timeZoneName')?.value;
    } catch {
      return undefined;
    }
  };
  const long = part('longGeneric');
  const short = part('short');
  if (long && short && !/^GMT/.test(long)) return long === short ? long : `${long} (${short})`;
  return short ?? tz;
}

/** "Fri, Oct 16" in the given zone. */
export function formatDayLabel(iso: string, tz: string): string {
  return new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: tz }).format(
    new Date(iso),
  );
}
